import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { format as dateFormat } from 'date-fns';

/**
 * Captures a chart canvas element as an image for PDF export
 * @param {HTMLCanvasElement} canvas - The canvas element to capture
 * @returns {string} - The base64 data URL of the image
 */
const captureCanvasImage = (canvas) => {
    if (!canvas) return null;
    return canvas.toDataURL('image/png');
};

/**
 * Creates a styled PDF document with header and basic formatting
 * @param {string} title - The title of the PDF document
 * @param {Object} options - Optional configuration
 * @returns {jsPDF} - The PDF document instance
 */
const createPdfDocument = (title, options = {}) => {
    const { orientation = 'portrait', unit = 'mm', format = 'a4' } = options;
    const doc = new jsPDF(orientation, unit, format);
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add title
    doc.setFontSize(18);
    doc.setTextColor(44, 62, 80);
    doc.text(title, pageWidth / 2, 20, { align: 'center' });

    // Add date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const dateText = `Generated on: ${dateFormat(new Date(), 'PPP')}`;
    doc.text(dateText, pageWidth / 2, 27, { align: 'center' });

    // Add horizontal line
    doc.setDrawColor(200, 200, 200);
    doc.line(14, 32, pageWidth - 14, 32);

    return doc;
};

/**
 * Adds a section title to the PDF
 * @param {jsPDF} doc - The PDF document instance
 * @param {string} title - The section title
 * @param {number} y - The y position to add the title
 * @returns {number} - The new y position after adding the title
 */
const addSectionTitle = (doc, title, y) => {
    doc.setFontSize(14);
    doc.setTextColor(52, 73, 94);
    doc.text(title, 14, y);
    doc.setDrawColor(230, 230, 230);
    doc.line(14, y + 2, 60, y + 2);
    return y + 10;
};

/**
 * Adds a chart image to the PDF with a caption
 * @param {jsPDF} doc - The PDF document instance
 * @param {string} imageData - The base64 image data URL
 * @param {string} caption - The chart caption
 * @param {number} y - The y position to add the chart
 * @param {Object} options - Optional configuration
 * @returns {number} - The new y position after adding the chart
 */
const addChartToPdf = (doc, imageData, caption, y, options = {}) => {
    const { width = 180, align = 'center' } = options;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Calculate image height while maintaining aspect ratio
    const imgProps = doc.getImageProperties(imageData);
    const imgHeight = (imgProps.height * width) / imgProps.width;

    // Check if we need a new page
    if (y + imgHeight + 20 > pageHeight) {
        doc.addPage();
        y = 20;
    }

    // Add the image
    const x = align === 'center' ? (pageWidth - width) / 2 : 14;
    doc.addImage(imageData, 'PNG', x, y, width, imgHeight);

    // Add caption
    y += imgHeight + 5;
    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text(caption, pageWidth / 2, y, { align: 'center' });

    return y + 15;
};

/**
 * Adds a metrics section with key-value pairs to the PDF
 * @param {jsPDF} doc - The PDF document instance
 * @param {Array} metrics - Array of metric objects with name and value properties
 * @param {number} y - The y position to add the metrics section
 * @returns {number} - The new y position after adding the metrics
 */
const addMetricsToPdf = (doc, metrics, y) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Check if we need a new page
    if (y + 20 + (metrics.length * 7) > pageHeight) {
        doc.addPage();
        y = 20;
    }

    // Add the metrics table using autoTable
    autoTable(doc, {
        startY: y,
        head: [['Metric', 'Value']],
        body: metrics.map(m => [m.name, m.value]),
        theme: 'grid',
        headStyles: {
            fillColor: [59, 130, 246],
            textColor: 255,
            fontStyle: 'bold'
        },
        styles: {
            overflow: 'linebreak',
            cellPadding: 4,
        },
        columnStyles: {
            0: { fontStyle: 'bold' },
        },
        margin: { left: 14, right: 14 }
    });

    return doc.lastAutoTable.finalY + 10;
};

/**
 * Exports a dashboard to PDF with charts and metrics
 * @param {string} title - The PDF title
 * @param {Array} charts - Array of chart objects with chartRef, caption, and optional width
 * @param {Array} metrics - Array of metric objects with name and value properties
 * @returns {jsPDF} - The generated PDF document
 */
export const exportDashboardToPdf = (title, charts = [], metrics = []) => {
    // Create PDF document
    const doc = createPdfDocument(title);
    let yPos = 40;

    // Add metrics section if provided
    if (metrics.length > 0) {
        yPos = addSectionTitle(doc, 'Key Metrics', yPos);
        yPos = addMetricsToPdf(doc, metrics, yPos);
    }

    // Add charts section if provided
    if (charts.length > 0) {
        yPos = addSectionTitle(doc, 'Charts & Visualizations', yPos);

        // Add each chart
        charts.forEach(chart => {
            if (chart.chartRef && chart.chartRef.current) {
                const canvas = chart.chartRef.current.canvas;
                const imageData = captureCanvasImage(canvas);
                if (imageData) {
                    yPos = addChartToPdf(
                        doc,
                        imageData,
                        chart.caption,
                        yPos,
                        { width: chart.width || 180 }
                    );
                }
            }
        });
    }

    return doc;
};

/**
 * Exports a dashboard to PDF with charts and metrics, then downloads it
 * @param {string} title - The PDF title
 * @param {Array} charts - Array of chart objects with chartRef, caption, and optional width
 * @param {Array} metrics - Array of metric objects with name and value properties
 */
export const downloadDashboardAsPdf = (title, charts = [], metrics = []) => {
    const doc = exportDashboardToPdf(title, charts, metrics);
    const fileName = `${title.toLowerCase().replace(/\s+/g, '-')}-${dateFormat(new Date(), 'yyyy-MM-dd')}.pdf`;
    doc.save(fileName);
}; 